import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	data,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	useActionData,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	GeneralErrorBoundary,
	statusHandlers,
} from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { checkLoginRateLimit } from '#app/utils/rate-limiting.server.ts'
import { isPublicSignupEnabled } from '#app/utils/signup-flag.server.ts'
import { PasswordSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './login.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	username: UsernameSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export const loader = async ({ request }: LoaderFunctionArgs) => {
	await requireAnonymous(request)
	return { signupEnabled: isPublicSignupEnabled() }
}

export const action = async ({ request }: ActionFunctionArgs) => {
	await requireAnonymous(request)

	// Check rate limit before processing
	const rateLimitResponse = checkLoginRateLimit(request)
	if (rateLimitResponse) throw rateLimitResponse

	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid username or password',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const { signupEnabled } = useLoaderData<typeof loader>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<>
			<p className="text-eyebrow text-ember">Welcome back</p>
			<h1 className="mt-4 font-display text-display-2">
				Pick up where you left off.
			</h1>
			<p className="mt-3 text-base leading-relaxed text-ink-fg-soft">
				Log in to continue your training.
			</p>

			<Form method="POST" {...getFormProps(form)} className="mt-10">
				<HoneypotInputs />
				<Field
					labelProps={{ children: 'Username' }}
					inputProps={{
						...getInputProps(fields.username, { type: 'text' }),
						autoFocus: true,
						className: 'lowercase',
						autoComplete: 'username',
					}}
					errors={fields.username.errors}
				/>

				<Field
					labelProps={{ children: 'Password' }}
					inputProps={{
						...getInputProps(fields.password, {
							type: 'password',
						}),
						autoComplete: 'current-password',
					}}
					errors={fields.password.errors}
				/>

				<div className="mt-2 flex items-baseline justify-between">
					<CheckboxField
						labelProps={{
							htmlFor: fields.remember.id,
							children: 'Remember me',
						}}
						buttonProps={getInputProps(fields.remember, {
							type: 'checkbox',
						})}
						errors={fields.remember.errors}
					/>
					<Link to="/forgot-password" className="ember-link text-sm">
						Forgot password?
					</Link>
				</div>

				<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
				<ErrorList errors={form.errors} id={form.errorId} />

				<StatusButton
					className="mt-8 w-full"
					status={isPending ? 'pending' : (form.status ?? 'idle')}
					type="submit"
					disabled={isPending}
				>
					Log in
				</StatusButton>
			</Form>

			{signupEnabled ? (
				<p className="mt-8 text-sm text-ink-fg-soft">
					New here?{' '}
					<Link
						to={
							redirectTo
								? `/signup?${encodeURIComponent(redirectTo)}`
								: '/signup'
						}
						className="ember-link"
					>
						Create an account
					</Link>
				</p>
			) : null}
		</>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Login to Momentum' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary statusHandlers={statusHandlers} />
}
